import React, {FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {Breadcrumb, Button, Dropdown, Layout, Menu, type MenuProps} from "antd";
import {
    BugOutlined,
    InfoCircleOutlined,
    LogoutOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    UserOutlined
} from "@ant-design/icons";
import {useLocalStorage} from "usehooks-ts";
import {AuthContext} from "../Contexts/AuthContext";
import {useShellBreadcrumbLabels} from "../Contexts/ShellBreadcrumbContext";
import {MenuDefinition} from "../Static/MenuDefinition";
import {hasOrganizerWorkspaceAccess} from "../Static/OrganizerRoles";
import {DeveloperFeedbackDialog} from "./DeveloperFeedbackDialog";
import {LanguageSwitch, type PublicLanguage} from "./ui";
import {resolveSelectedMenuKeys} from "./leftMainMenuSelection";
import {buildShellBreadcrumbs} from "./appShellBreadcrumbs";
import "../Styles/AppShell.css";

import expandedLogo from "../Images/ati-logo-negativ.png";
import collapsedLogo from "../Images/logo-samo-gore.png";

const {Header, Sider, Content} = Layout;

// Same key the previous top bar used, so organizers keep their saved sidebar state.
const COLLAPSED_STORAGE_KEY = "top-bar-collapsed";
// Matches the antd `lg` breakpoint that Sider uses below.
const MOBILE_QUERY = "(max-width: 991.98px)";
const NAVIGATION_ID = "app-shell-navigation";

const isMobileViewport = () =>
    typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(MOBILE_QUERY).matches;

const normalizeLanguage = (language?: string): PublicLanguage =>
    language?.split("-")[0]?.toLowerCase() === "en" ? "en" : "hr";

export const AppShell: FC<PropsWithChildren> = ({children}) => {
    const {t, i18n} = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const {auth: {userName, roles}, signOut} = useContext(AuthContext);
    const breadcrumbLabels = useShellBreadcrumbLabels();
    const [storedCollapsed, setStoredCollapsed] = useLocalStorage(COLLAPSED_STORAGE_KEY, true);
    const [isMobile, setIsMobile] = useState(isMobileViewport);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [feedbackOpen, setFeedbackOpen] = useState(false);

    const collapsed = isMobile ? !mobileOpen : storedCollapsed;
    const homeRoute = hasOrganizerWorkspaceAccess(roles) ? "/Admin/Events" : "/";
    const activeLanguage = normalizeLanguage(i18n.language);

    const closeMobileMenu = useCallback(() => setMobileOpen(false), []);
    const toggleMenu = () => isMobile ? setMobileOpen(open => !open) : setStoredCollapsed(!storedCollapsed);

    // Close the overlay menu whenever the route changes (menu, breadcrumb or in-page link).
    useEffect(closeMobileMenu, [closeMobileMenu, location.pathname, location.search]);

    useEffect(() => {
        if (!isMobile || !mobileOpen) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                closeMobileMenu();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [closeMobileMenu, isMobile, mobileOpen]);

    const menuItems = useMemo<MenuProps["items"]>(() => MenuDefinition(roles).map(item => {
        const icon = item.smallIcon ?? item.icon;

        if (!item.children?.length) {
            return {key: item.route, icon, label: t(item.translationKey)};
        }

        return {
            // A dedicated section key avoids duplicating child keys, which use the real route.
            key: `${item.route}-section`,
            icon,
            label: t(item.translationKey),
            children: item.children.map(child => ({
                key: child.route,
                icon: child.smallIcon ?? child.icon,
                label: t(child.translationKey)
            }))
        };
    }), [roles, t]);
    const selectedMenuKeys = useMemo(
        () => resolveSelectedMenuKeys(location.pathname, location.search),
        [location.pathname, location.search]
    );
    const breadcrumbItems = useMemo(
        () => buildShellBreadcrumbs(location.pathname, location.search, breadcrumbLabels, t).map(crumb => ({
            key: crumb.key,
            title: crumb.to ? <Link to={crumb.to}>{crumb.title}</Link> : <span aria-current="page">{crumb.title}</span>
        })),
        [breadcrumbLabels, location.pathname, location.search, t]
    );
    const userMenuItems: MenuProps["items"] = [
        {key: "profile", icon: <UserOutlined/>, label: t("profile", {defaultValue: "Profile"}), onClick: () => navigate("/Admin/Profile")},
        {key: "report-problem", icon: <BugOutlined/>, label: t("developer-feedback-title", {defaultValue: "Report a problem"}), onClick: () => setFeedbackOpen(true)},
        {key: "about-app", icon: <InfoCircleOutlined/>, label: t("about-app-title", {defaultValue: "About the app"}), onClick: () => navigate("/Admin/About")},
        {type: "divider"},
        {key: "logout", icon: <LogoutOutlined/>, label: t("logout", {defaultValue: "Logout"}), onClick: signOut}
    ];
    const displayName = userName?.trim() || "-";
    const menuToggleLabel = collapsed
        ? t("app-shell.open-menu", {defaultValue: "Open menu"})
        : t("app-shell.close-menu", {defaultValue: "Close menu"});

    return (
        <Layout className={`app-shell${isMobile ? " app-shell--mobile" : ""}`}>
            <Sider
                id={NAVIGATION_ID}
                className="app-shell__sider"
                breakpoint="lg"
                collapsedWidth={isMobile ? 0 : 80}
                collapsible
                collapsed={collapsed}
                onBreakpoint={setIsMobile}
                trigger={null}
                width={250}
            >
                <Link
                    to={homeRoute}
                    className="app-shell__logo"
                    aria-label={t("app-shell.home", {defaultValue: "ATIventura home"})}
                >
                    <img className="app-shell__logo-image app-shell__logo-image--expanded" src={expandedLogo} alt=""/>
                    <img className="app-shell__logo-image app-shell__logo-image--collapsed" src={collapsedLogo} alt=""/>
                </Link>
                <nav aria-label={t("app-shell.main-navigation", {defaultValue: "Main navigation"})}>
                    <Menu
                        theme="dark"
                        mode="inline"
                        items={menuItems}
                        selectedKeys={selectedMenuKeys}
                        onClick={({key}) => navigate(key)}
                    />
                </nav>
            </Sider>
            {isMobile && mobileOpen ? <div className="app-shell__backdrop" aria-hidden="true" onClick={closeMobileMenu}/> : null}
            <Layout className="app-shell__main">
                <Header className="app-shell__header">
                    <Button
                        className="app-shell__menu-toggle"
                        type="text"
                        icon={collapsed ? <MenuUnfoldOutlined/> : <MenuFoldOutlined/>}
                        aria-label={menuToggleLabel}
                        aria-controls={NAVIGATION_ID}
                        aria-expanded={!collapsed}
                        onClick={toggleMenu}
                    />
                    <Breadcrumb
                        className="app-shell__breadcrumb"
                        aria-label={t("app-shell.breadcrumb", {defaultValue: "Breadcrumb"})}
                        items={breadcrumbItems}
                    />
                    <div className="app-shell__actions">
                        <LanguageSwitch
                            label={t("app-shell.language", {defaultValue: "Language"})}
                            value={activeLanguage}
                            onChange={language => {
                                if (language !== activeLanguage) {
                                    void i18n.changeLanguage(language);
                                }
                            }}
                        />
                        <Dropdown menu={{items: userMenuItems}} trigger={["click"]} placement="bottomRight">
                            <Button
                                className="app-shell__user"
                                type="text"
                                icon={<UserOutlined/>}
                                aria-label={`${t("app-shell.account-menu", {defaultValue: "Account menu"})}: ${displayName}`}
                            >
                                <span className="app-shell__user-name">{displayName}</span>
                            </Button>
                        </Dropdown>
                    </div>
                </Header>
                <Content className="app-shell__content">
                    {children}
                </Content>
            </Layout>
            <DeveloperFeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)}/>
        </Layout>
    );
};

export default AppShell;
